import { Button } from "@devhop/ui";
import {
  RiAddLine,
  RiCheckLine,
  RiCloseLine,
  RiExternalLinkLine,
} from "@remixicon/react";
import type {
  AdminCatalogItem,
  AdminCatalogProduct,
} from "@/modules/cf-feed/presentation/api/types";
import { useBoundedInfiniteScroll } from "@/modules/cf-feed/presentation/lib/use-bounded-infinite-scroll";
import {
  WorkspaceProductCard,
  type WorkspaceProductCardAction,
} from "./WorkspaceProductCard";
import { kip } from "./WorkspaceProductEditor";

function catalogCode(item: AdminCatalogItem) {
  return item.defaultCfCode ?? item.suggestedCfCode ?? item.sku;
}

export function WorkspaceCatalogCards({
  products,
  selectedItems,
  disabled = false,
  commitDisabled = false,
  readOnly = false,
  filtered = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  onSelectionChange,
  onCommit,
  onClear,
  onViewProduct,
  allowWholeProductCommitWithSelection = false,
  reviewBeforeCommit = false,
}: {
  products: AdminCatalogProduct[];
  selectedItems: AdminCatalogItem[];
  disabled?: boolean;
  commitDisabled?: boolean;
  readOnly?: boolean;
  filtered?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onSelectionChange: (items: AdminCatalogItem[], checked: boolean) => void;
  onCommit: (items: AdminCatalogItem[]) => void;
  onClear: () => void;
  onViewProduct?: (product: AdminCatalogProduct) => void;
  allowWholeProductCommitWithSelection?: boolean;
  reviewBeforeCommit?: boolean;
}) {
  const selectedIds = new Set(selectedItems.map((item) => item.variantId));
  const { scrollRootRef, sentinelRef } = useBoundedInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });

  return (
    <>
      <div
        ref={scrollRootRef}
        className="grid max-h-[42rem] grid-cols-2 gap-2 overflow-auto overscroll-contain 2xl:grid-cols-3"
      >
        {products.map((product) => {
          const chosen = product.variants.filter((item) =>
            selectedIds.has(item.variantId),
          );
          const toCommit = chosen.length ? chosen : product.variants;
          const minPrice = Math.min(
            ...product.variants.map((item) => Number(item.price)),
          );
          const maxPrice = Math.max(
            ...product.variants.map((item) => Number(item.price)),
          );
          const actions: WorkspaceProductCardAction[] = readOnly
            ? [
                {
                  id: "view",
                  label: "ເບິ່ງໃນຄິວຂາຍ",
                  icon: <RiExternalLinkLine className="size-4" aria-hidden />,
                  onSelect: () => onViewProduct?.(product),
                },
              ]
            : [
                {
                  id: "commit",
                  label: reviewBeforeCommit
                    ? chosen.length
                      ? `ເບິ່ງລາຄາທີ່ເລືອກ (${chosen.length})`
                      : `ເບິ່ງລາຄາກ່ອນເພີ່ມ (${product.variants.length})`
                    : chosen.length
                      ? `ເພີ່ມທີ່ເລືອກ (${chosen.length})`
                      : filtered
                        ? `ເພີ່ມທີ່ພົບ (${product.variants.length})`
                        : `ເພີ່ມທັງສິນຄ້າ (${product.variants.length})`,
                  icon: reviewBeforeCommit ? (
                    <RiCheckLine className="size-4" aria-hidden />
                  ) : (
                    <RiAddLine className="size-4" aria-hidden />
                  ),
                  disabled: disabled || commitDisabled || toCommit.length > 100,
                  onSelect: () => onCommit(toCommit),
                },
                ...(chosen.length > 0 && allowWholeProductCommitWithSelection
                  ? [
                      {
                        id: "commit-all",
                        label: reviewBeforeCommit
                          ? `ເບິ່ງລາຄາທັງສິນຄ້າ (${product.variants.length})`
                          : `ເພີ່ມທັງສິນຄ້າ (${product.variants.length})`,
                        icon: reviewBeforeCommit ? (
                          <RiCheckLine className="size-4" aria-hidden />
                        ) : (
                          <RiAddLine className="size-4" aria-hidden />
                        ),
                        disabled:
                          disabled ||
                          commitDisabled ||
                          product.variants.length > 100,
                        onSelect: () => onCommit(product.variants),
                      } satisfies WorkspaceProductCardAction,
                    ]
                  : []),
              ];

          return (
            <WorkspaceProductCard
              key={product.productId}
              productId={product.productId}
              name={product.name}
              imageUrl={
                product.variants.find((item) => item.imageUrl)?.imageUrl
              }
              subtitle={
                <>
                  {readOnly ? "ເພີ່ມເຂົ້າງານແລ້ວ" : "ຕົວເລືອກທີ່ຍັງບໍ່ເພີ່ມ"}
                  {" · "}
                  {product.variants.slice(0, 3).map(catalogCode).join(" · ")}
                  {product.variants.length > 3
                    ? ` · +${product.variants.length - 3}`
                    : ""}
                </>
              }
              price={
                <>
                  {minPrice === maxPrice
                    ? kip(minPrice)
                    : `${kip(minPrice)} – ${kip(maxPrice)}`}
                  {chosen.length > 0 && !readOnly
                    ? ` · ເລືອກ ${chosen.length}`
                    : ""}
                </>
              }
              variants={product.variants.map((item) => ({
                id: item.variantId,
                selectionLabel: `ເລືອກ ${catalogCode(item)}`,
                content: (
                  <>
                    <strong className="text-primary">
                      {catalogCode(item)}
                    </strong>
                    <span className="block truncate text-muted-foreground text-xs">
                      {item.sku} ·{" "}
                      {item.isPreorder
                        ? "Preorder"
                        : item.availableToAdd <= 0
                          ? readOnly
                            ? "ສະຕັອກ 0"
                            : "ສະຕັອກ 0 · ເລືອກໄດ້"
                          : `ຄົງເຫຼືອ ${item.availableToAdd}`}
                    </span>
                  </>
                ),
                desktopPrice: (
                  <strong className="text-sm tabular-nums">
                    {kip(Number(item.price))}
                  </strong>
                ),
                mobilePrice: kip(Number(item.price)),
              }))}
              selectedVariantIds={readOnly ? undefined : selectedIds}
              onSelectionChange={
                readOnly
                  ? undefined
                  : (variantIds, checked) =>
                      onSelectionChange(
                        product.variants.filter((item) =>
                          variantIds.includes(item.variantId),
                        ),
                        checked,
                      )
              }
              selectionDisabled={disabled}
              leading={
                readOnly ? (
                  <RiCheckLine className="size-5 text-primary" aria-hidden />
                ) : null
              }
              actions={actions}
            />
          );
        })}
        {hasNextPage ? (
          <div
            ref={sentinelRef}
            className="col-span-full flex min-h-10 items-center justify-center text-muted-foreground text-xs"
            aria-live="polite"
          >
            {isFetchingNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ເລື່ອນເພື່ອໂຫຼດເພີ່ມ"}
          </div>
        ) : null}
      </div>
      {!readOnly && selectedItems.length > 0 ? (
        <section
          className="sticky bottom-0 z-20 space-y-2 rounded-xl border bg-background p-3 shadow-lg"
          aria-label="ສິນຄ້າທີ່ເລືອກ"
        >
          <details>
            <summary className="min-h-11 cursor-pointer py-3 font-medium text-sm">
              ເລືອກ {new Set(selectedItems.map((item) => item.productId)).size}{" "}
              ສິນຄ້າ · {selectedItems.length}/100 SKU · ເບິ່ງລາຍການ
            </summary>
            <ul className="max-h-48 overflow-y-auto">
              {selectedItems.map((item) => (
                <li
                  key={item.variantId}
                  className="flex items-center justify-between gap-2 border-t text-sm"
                >
                  <span className="min-w-0 break-words">
                    {item.name} · {catalogCode(item)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    className="size-11 shrink-0"
                    disabled={disabled}
                    aria-label={`ຍົກເລີກເລືອກ ${item.sku}`}
                    onClick={() => onSelectionChange([item], false)}
                  >
                    <RiCloseLine size={18} aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          </details>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="min-h-11 flex-1"
              disabled={disabled || commitDisabled}
              onClick={() => onCommit(selectedItems)}
            >
              {reviewBeforeCommit ? (
                <RiCheckLine size={18} aria-hidden />
              ) : (
                <RiAddLine size={18} aria-hidden />
              )}
              {reviewBeforeCommit
                ? `ກວດລາຄາກ່ອນເພີ່ມ (${selectedItems.length})`
                : `ເພີ່ມທີ່ເລືອກ (${selectedItems.length})`}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={disabled}
              onClick={onClear}
            >
              ລ້າງທີ່ເລືອກ
            </Button>
          </div>
        </section>
      ) : null}
    </>
  );
}
